import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import { Button, Checkbox, Icon, Response } from '../../components';
import { SelectionPopup } from '../../components/SelectionPopup';
import {
    SelectedDataMulti,
    SelectedItem,
    SelectedItemAsset,
    SelectedItemExtended,
    SelectedItemLegalType,
} from '../../components/SelectionPopup/SelectionPopup';
import { useDiagramStore } from '../../store/useDiagramStore';
import axios from '../../utils/axios';

import styles from './tax-optimisation.module.css';
import TaxBox from './components/TaxBox/TaxBox';
import Loader from '../../components/Loader/Loader';
import TransitionComponent from '../../components/TransitionComponent/TransitionComponents';
import BooleanImage from '../../components/BooleanImage/BooleanImage';
import { Countries, GraphTypes, TCountryCode } from '../../types/general';
import { DealTaxesSelectedData } from '../DealTaxes/DealTaxes';
import TaxOptimisationSelection from './components/TaxOptimisationSelection';
import { dispatchAsidePortalContentChanged } from '../../utils/aside-portal-event';
import { createPortal } from 'react-dom';
import { useMenuStore } from '../../store/useMenuStore';
import { LinkedButton } from '../../components/Button';
import { Icons } from '../../components/Icon/Icon';
import LoaderComponent from '../../components/TransitionComponent/LoaderContent';

export interface TaxSortedPerStructure {
    groupId: number;
    id: number;
    domicile: string;
    fund: string;
    legalType: string;
    ifOpenEnded: boolean;
    bookingType: string;
    fundStatus: string;
    return: number;
    fundRegime: boolean;
    selfManaged: boolean;
    ifBusinessAssets: boolean;
    legalTypeId: number;
    link?: string;

    furtherStatus?: string;

    investmentFund?: SelectedItem | number;
}

export interface Taxes {
    frontEndLabels: {
        groupId: number[];
        uniqueReturns: number[];
        domicile: string[];
        legalType: string[];
        openEnded: string[];
        fundStatus: string[][];
        bookingType: string[][];
        fund: string[];
    };
    sortedTaxesPerStructure: TaxSortedPerStructure[];
}

export interface TaxOptimisationSelectedData {
    investors: SelectedItemExtended[];
    assets: SelectedItemAsset[];
    selectedInvestor: undefined | SelectedItemExtended;
    initialInvestment: number;
    returnOnAsset: number;
    distribution: number;
}

const TaxOptimisation = () => {
    const [pageReady, setPageReady] = useState(false);
    const [controller, setController] = useState(new AbortController());

    const [selectedData, setSelectedData] = useState<TaxOptimisationSelectedData>({
        investors: [],
        assets: [],
        selectedInvestor: undefined,
        initialInvestment: 100,
        returnOnAsset: 20,
        distribution: 10,
    });

    const [selectedItems, setSelectedItems] = useState<{
        type: string;
        items: number[];
    }>({
        type: 'group',
        items: [],
    });

    const [labels, setLabels] = useState<undefined | Taxes>();

    const [groups, setGroups] = useState<
        | undefined
        | {
              [key: string]: TaxSortedPerStructure[];
          }
    >(undefined);

    const [setGraphStoreData] = useDiagramStore((state) => [state.setStoreData]);

    const [error, setError] = useState<boolean>(false);

    const [showPopup, setShowPopup] = useState(false);
    const [toggleRequest, setToggleRequest] = useState(false);

    const navigation = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isTaxOpen, setIsTaxOpen] = useState<boolean>(false);
    const [openTaxIndex, setOpenTaxIndex] = useState<undefined | number>();

    const [loading, setLoading] = useState(false);

    const [isSelecting, setIsSelecting] = useState(false);
    // const [isPreset, setIsPreset] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useMenuStore((state) => [state.isOpen, state.setIsOpen]);
    const [selectedDetail, setSelectedDetail] = useState<TaxSortedPerStructure | undefined>();

    const [selectedGroup, setSelectedGroup] = useState();

    useEffect(() => {
        if (!searchParams.toString().length) {
            (async () => {
                await initPage();
            })();
        }
    }, [searchParams]);

    useEffect(() => {
        setGraphStoreData({
            useInvestmentStructure: false,
            graphType: GraphTypes.Optimisation,
        });
        (async () => {
            await initPage();
        })();
    }, []);

    useEffect(() => {
        if (showPopup) {
            setLoading(true);
            // setPageReady(true);
            setOpenTaxIndex(undefined);
            setIsTaxOpen(false);
            dispatchAsidePortalContentChanged(false);
        }
    }, [showPopup]);

    useEffect(() => {
        const getData = setTimeout(() => {
            if (toggleRequest) {
                (async () => {
                    await makeRequest();
                })();
            }
        }, 400);

        return () => clearTimeout(getData);
    }, [toggleRequest]);

    useEffect(() => {
        setSelectedDetail(
            typeof openTaxIndex !== 'undefined' && !!groups && groups[Object.keys(groups)[openTaxIndex]]
                ? groups[Object.keys(groups)[openTaxIndex]][0]
                : undefined,
        );
    }, [groups, openTaxIndex]);

    // useEffect(() => {
    //     // Clear portal content when leaving the page
    //     return () => {
    //         // dispatchAsidePortalContentChanged(false);
    //     };
    // }, []);

    const initPage = async () => {
        const investors = searchParams.get('investors');
        const assets = searchParams.get('assets');

        const initialInvestment = searchParams.get('initialInvestment');
        const returnOnAsset = searchParams.get('returnOnAsset');
        const distribution = searchParams.get('distribution');

        const openTaxIndexParam = searchParams.get('openTaxIndex');

        const selectedTypeParam = searchParams.get('selectedType');
        const selectedItemsParam = searchParams.get('selectedItems');

        const selectedInvestor = searchParams.get('selectedInvestor');

        if (!investors || !assets) {
            setShowPopup(true);
            return;
        } else {
            setPageReady(false);
        }

        if (openTaxIndexParam) {
            setOpenTaxIndex(+openTaxIndexParam);
            setIsTaxOpen(true);
        }

        if (selectedTypeParam && selectedItemsParam) {
            setSelectedItems({
                type: selectedTypeParam,
                items: (JSON.parse(selectedItemsParam) || []).map((item: string) => +item),
            });
            // setIsPreset(true);
            // setIsSelecting(true);
        } else {
            // setIsPreset(false);
            disableSelection();
        }

        // setInvestors(investorCountry);

        setSelectedData({
            ...selectedData,
            investors: JSON.parse(investors),
            assets: JSON.parse(assets),
            selectedInvestor: selectedInvestor ? JSON.parse(selectedInvestor) : undefined,
            initialInvestment: initialInvestment ? +initialInvestment : 100,
            returnOnAsset: returnOnAsset ? +returnOnAsset : 20,
            distribution: distribution ? +distribution : 10,
        });

        setToggleRequest(true);

        setPageReady(true);
    };

    const getItemUrl = (item: TaxSortedPerStructure) => {
        const tmp: DealTaxesSelectedData = {
            ...selectedData,
            assets: selectedData.assets,
            investors: selectedData.investors,
            investmentFund: { ...(item.investmentFund as SelectedItemLegalType), legalTypeId: item.legalTypeId },
            fundOfFunds: undefined,
            selectedInvestor: selectedData.selectedInvestor,
            openEnded: item.ifOpenEnded,
            // corporate: item.legalType === 'corporate',
            // ifCommercialPartnership: item.furtherStatus === 'n/a' ? undefined : item.furtherStatus == 'commercial',
            fundStatus: item.furtherStatus === 'ko' || item.furtherStatus === 'n/a' ? undefined : item.furtherStatus,
            ifBusinessAssets: item.ifBusinessAssets || undefined,
            booked: item.bookingType === 'n/a' ? undefined : item.bookingType == 'at NAV',
        };

        // const entries = Object.entries(tmp).map((item) =>
        //     item.map((sub) => (sub !== null && typeof sub !== 'undefined' ? sub.toString() : ''))
        // );
        const tableUrl = new URLSearchParams(
            // @ts-ignore
            Object.entries(tmp).map((entry) => {
                // @ts-ignore
                entry[1] = typeof entry[1] !== 'undefined' ? JSON.stringify(entry[1]) : undefined;
                return entry;
            }),
        );

        tableUrl.forEach((value, name) => {
            if (typeof value === 'undefined' || value === 'undefined') {
                tableUrl.delete(name);
            }
        });

        return tableUrl.toString();
    };

    const makeRequest = async () => {
        if (!pageReady) {
            // setOpenTaxIndex(undefined);
            setLoading(true);
        }

        dispatchAsidePortalContentChanged(true);

        setToggleRequest(false);

        if (controller) {
            controller.abort();
        }

        const newController = new AbortController();
        setController(newController);

        try {
            const { data } = await axios.get<Taxes>('/tax-optimisation', {
                signal: newController.signal,
                params: selectedData,
            });

            // @ts-ignore
            data.sortedTaxesPerStructure = data.sortedTaxesPerStructure.map((item) => ({
                ...item,
                investmentFund: {
                    id: item.investmentFund,
                    name: item.fund,
                    countryCode: item.domicile as TCountryCode,
                    countryName: '',
                    countryId: '',
                },
                fundOfFunds: undefined,
            }));

            if (selectedItems.items.length) {
                data.sortedTaxesPerStructure = data.sortedTaxesPerStructure?.filter((item) => {
                    return selectedItems.items.includes(selectedItems.type === 'single' ? item.id : item.groupId);
                });
            }

            const groupsTmp = data.sortedTaxesPerStructure?.reduce(function (obj, item) {
                item.link = `${getItemUrl(item)}&optimisation=true`;
                // @ts-ignore
                (obj[item.groupId] = obj[item.groupId] || []).push(item);
                return obj;
            }, {});

            setGroups(groupsTmp);
            setLabels(data);

            // Dispatch event once data is loaded from API
            // const shouldRenderPortal = document.querySelector('#aside');
            // dispatchAsidePortalContentChanged(!!shouldRenderPortal);
        } catch (e) {
            setGroups(undefined);
            setLabels(undefined);

            // Dispatch event when data fails to load
            // dispatchAsidePortalContentChanged(false);
        } finally {
            setTimeout(() => {
                setLoading(false);

                setPageReady(true);
            }, 100);
        }

        // setOpenTaxIndex(undefined);
        disableSelection();

        setUrlParams(selectedData);
    };

    const handleInputChange = (prop: string, value: number) => {
        setSelectedData({ ...selectedData, [prop]: value });

        // if (selectedData.assets.currentAssetTaxId) {
        //     setToggleRequest(true);
        // }
    };

    const handleFormSubmission = async (formSelectedData: SelectedDataMulti) => {
        setGroups(undefined);
        setShowPopup(false);

        setSelectedData({
            ...selectedData,
            ...formSelectedData,
            selectedInvestor: formSelectedData.investors[0],
        });

        setToggleRequest(true);
    };

    const enableSelection = () => {
        setIsSelecting(true);
    };

    const disableSelection = () => {
        setIsSelecting(false);
        setSelectedItems({
            type: selectedItems.type,
            items: [],
        });
    };

    const handleSelect = (data: TaxSortedPerStructure) => {
        const id = typeof openTaxIndex !== 'undefined' ? data.id : data.groupId;
        const tmp = selectedItems.items.filter((i: number) => i !== id);
        if (tmp.length === selectedItems.items.length) {
            tmp.push(id);
        }

        setSelectedItems({
            type: typeof openTaxIndex !== 'undefined' ? 'single' : 'group',
            items: tmp,
        });
    };

    const savePreset = async (e: any) => {
        e.preventDefault();

        try {
            await axios.post('/presets/tax-optimisation', {
                ...selectedData,
                selectedType: selectedItems.type,
                selectedItems: selectedItems.items,
            });

            // redirect
            navigation('/dashboard?save=1');
        } catch (e) {
            setError(true);
        }
    };

    const validateSelection = async () => {
        return true;
    };

    const setUrlParams = (selectedData: TaxOptimisationSelectedData) => {
        const tableUrl = new URLSearchParams();

        for (const entry of Object.entries(selectedData)) {
            // @ts-ignore
            if (typeof entry[1] !== 'undefined' && entry[1] !== 'undefined') {
                tableUrl.set(entry[0], JSON.stringify(entry[1]));
            }
        }

        // setTableUrl('?' + tableUrl.toString());

        // if (!isOptimisation) {
        setSearchParams(tableUrl.toString());

        // return;
        // }
    };

    const handleSelectedInvestor = (id: number) => () => {
        setLoading(true);
        setSelectedData({
            ...selectedData,
            selectedInvestor: selectedData.investors.find((i) => i.id === id),
        });

        setGroups(undefined);
        setShowPopup(false);

        setToggleRequest(true);
    };

    const handleSelectAll = () => {};

    return (
        <>
            <TransitionComponent inProp={showPopup} isAbsolute={true} duration={100}>
                <SelectionPopup isMulti={true} onSubmit={handleFormSubmission} validateSelection={validateSelection} />
            </TransitionComponent>

            {pageReady && (
                // <TransitionComponent inProp={pageReady}>
                <div className={styles.content}>
                    {/*<ResultTableLeft*/}
                    {/*    // tableInfo={<BaseInputs selectedData={selectedData} setSelectedData={handleInputChange} />}*/}
                    {/*    hideFundRow={true}*/}
                    {/*></ResultTableLeft>*/}

                    {pageReady &&
                        !showPopup &&
                        document.querySelector('#menuAsideContent') &&
                        createPortal(
                            <TaxOptimisationSelection
                                selectedData={selectedData}
                                setSelectedData={handleInputChange}
                                handleSelectedInvestor={handleSelectedInvestor}
                                active={true}
                                setActive={setIsMenuOpen}
                                hidden={showPopup}
                                onEdit={() => {
                                    setShowPopup(true);
                                }}
                                selectedDetails={selectedDetail}
                            />,
                            document.querySelector('#menuAsideContent')!,
                        )}

                    <div className={`${styles.groupsContainer} ${isMenuOpen ? styles.collapsed : ''}`}>
                        <LoaderComponent inProp={!loading}>
                            {/*{isSelecting && (*/}
                            {/*    <div className={styles.groupTitle}>*/}
                            {/*        <h3>Customise items to be stored</h3>*/}
                            {/*    </div>*/}
                            {/*)}*/}
                            {isTaxOpen ? (
                                <>
                                    <div className={`${styles.selectedHeader}`}>
                                        <h1 className={`text-grey ${styles.selectedTitle}`}>
                                            <span className={'highlight-secondary'}>Tax optimisation</span>: expanded
                                            view
                                        </h1>

                                        <Button
                                            onClick={() => {
                                                setOpenTaxIndex(undefined);
                                                setIsTaxOpen(false);
                                            }}
                                            after={<Icon icon={Icons.return} />}
                                            size={'sm'}
                                        >
                                            Back to short view
                                        </Button>
                                    </div>

                                    {groups &&
                                        Object.keys(groups).map((key, index) => (
                                            <div
                                                key={index}
                                                className={`
                                                ${styles.tableContent} 
                                                ${openTaxIndex === index ? styles.open : ''}
                                                `}
                                            >
                                                <h3
                                                    onClick={() => {
                                                        setOpenTaxIndex(index === openTaxIndex ? undefined : index);
                                                    }}
                                                >
                                                    <button>
                                                        <Icon icon={Icons.chevronDownFilled} />
                                                    </button>
                                                    {index + 1}. Return on investment:{' '}
                                                    {labels &&
                                                        (
                                                            (labels.frontEndLabels.uniqueReturns[index] || 0) * 100
                                                        ).toFixed(4)}
                                                    %
                                                </h3>
                                                <div className={styles.tableWrapper}>
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                {isSelecting && (
                                                                    <th>
                                                                        <Checkbox
                                                                            label={''}
                                                                            value={''}
                                                                            checked={selectedGroup == index}
                                                                            onChange={() => {
                                                                                handleSelectAll();
                                                                            }}
                                                                            useMargin={false}
                                                                        />
                                                                    </th>
                                                                )}
                                                                <th>Fund structure</th>
                                                                <th>Domicile</th>
                                                                <th>Legal type</th>
                                                                <th>Legal form(s)</th>
                                                                <th>Liquidity</th>
                                                                <th>Other parameters</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {groups[key].map((item, itemIndex) => (
                                                                <tr
                                                                    key={`row-${itemIndex}`}
                                                                    onClick={() => {
                                                                        if (isSelecting) {
                                                                            handleSelect(item);
                                                                        }
                                                                    }}
                                                                >
                                                                    {isSelecting && (
                                                                        <td>
                                                                            <Checkbox
                                                                                label={''}
                                                                                value={itemIndex}
                                                                                checked={
                                                                                    !!selectedItems.items.find(
                                                                                        (i: number) =>
                                                                                            i ===
                                                                                            (isTaxOpen
                                                                                                ? item.id
                                                                                                : item.groupId),
                                                                                    )
                                                                                }
                                                                                onChange={() => {
                                                                                    handleSelect(item);
                                                                                }}
                                                                                useMargin={false}
                                                                            />
                                                                        </td>
                                                                    )}
                                                                    <td>{item.fund}</td>
                                                                    <td>
                                                                        {item.domicile?.toLowerCase() === 'various'
                                                                            ? labels?.frontEndLabels?.domicile[index]
                                                                            : Countries[
                                                                                  item.domicile?.toLowerCase() as TCountryCode
                                                                              ]?.domicileName ||
                                                                              labels?.frontEndLabels?.domicile[index]}
                                                                    </td>
                                                                    <td>{item.legalType}</td>
                                                                    <td></td>
                                                                    <td>
                                                                        {item.ifOpenEnded
                                                                            ? 'open-ended'
                                                                            : 'close-ended'}
                                                                    </td>
                                                                    <td></td>
                                                                    <td>
                                                                        <LinkedButton
                                                                            color={'neutral'}
                                                                            to={`/deal-taxes?${item.link}&openTaxIndex=${openTaxIndex}`}
                                                                            size={'sm'}
                                                                            after={<Icon icon={Icons.link} />}
                                                                        >
                                                                            Detailed View
                                                                        </LinkedButton>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ))}
                                </>
                            ) : (
                                <div className={`${styles.groups} ${openTaxIndex !== undefined ? styles.open : ''}`}>
                                    {groups &&
                                        Object.keys(groups).map((key, index) => (
                                            <div
                                                className={`${styles.group} ${
                                                    openTaxIndex === index ? styles.open : styles.closed
                                                } ${
                                                    openTaxIndex !== undefined && openTaxIndex !== index
                                                        ? styles.hidden
                                                        : ''
                                                }`}
                                                key={`group-${index}`}
                                            >
                                                <div className={styles.groupTitle}>
                                                    <h5>
                                                        <span>
                                                            <span className={styles.groupTitleNumber}>{index + 1}</span>
                                                            Return on investment
                                                        </span>
                                                        <span>
                                                            {labels &&
                                                                (
                                                                    (labels.frontEndLabels.uniqueReturns[index] || 0) *
                                                                    100
                                                                ).toFixed(4)}
                                                            %
                                                        </span>
                                                    </h5>
                                                </div>
                                                {/*<div*/}
                                                {/*    className={`${styles.groupItems} ${*/}
                                                {/*        openTaxIndex === index ? styles.open : styles.closed*/}
                                                {/*    }`}*/}
                                                {/*    style={*/}
                                                {/*        {*/}
                                                {/*            '--total': Math.min(Math.max(groups[key].length - 1, 0), 3),*/}
                                                {/*        } as React.CSSProperties*/}
                                                {/*    }*/}
                                                {/*    onClick={() => {*/}
                                                {/*        if (isSelecting && !openTaxIndex) {*/}
                                                {/*            handleSelect(groups[key][0]);*/}
                                                {/*        } else {*/}
                                                {/*            setOpenTaxIndex(index);*/}
                                                {/*            setIsTaxOpen(true);*/}
                                                {/*        }*/}
                                                {/*    }}*/}
                                                {/*>*/}
                                                {groups[key].slice(0, 1).map((item, itemIndex) => (
                                                    <React.Fragment key={itemIndex}>
                                                        <TaxBox
                                                            data={item}
                                                            img={'./images/thumb-taxes.png'}
                                                            name={'Deal taxes'}
                                                            link={`deal-taxes?${item.link}&openTaxIndex=${openTaxIndex}`}
                                                            key={`group-item-${index}-${itemIndex}`}
                                                            isOpen={isTaxOpen}
                                                            collapsedOpenDomicile={
                                                                labels?.frontEndLabels?.domicile[index]
                                                            }
                                                            collapsedOpenEnded={
                                                                labels?.frontEndLabels?.openEnded[index]
                                                            }
                                                            collapsedLegalType={
                                                                labels?.frontEndLabels?.legalType[index]
                                                            }
                                                            collapsedFund={labels?.frontEndLabels?.fund[index]}
                                                            hasLink={false}
                                                            isSelecting={isSelecting}
                                                            isSelected={
                                                                !!selectedItems.items.find(
                                                                    (i: number) =>
                                                                        i === (isTaxOpen ? item.id : item.groupId),
                                                                )
                                                            }
                                                            handleSelect={handleSelect}
                                                            stackItems={Math.min(
                                                                Math.max(groups[key].length - 1, 0),
                                                                3,
                                                            )}
                                                            onClick={() => {
                                                                if (isSelecting && !openTaxIndex) {
                                                                    handleSelect(groups[key][0]);
                                                                } else {
                                                                    setOpenTaxIndex(index);
                                                                    setIsTaxOpen(true);
                                                                }
                                                            }}
                                                        />
                                                    </React.Fragment>
                                                ))}
                                                {/*</div>*/}
                                            </div>
                                        ))}
                                </div>
                            )}
                        </LoaderComponent>

                        <TransitionComponent inProp={!loading} className={styles.actions}>
                            <>
                                {isSelecting && (
                                    <>
                                        <Button outlined={true} onClick={disableSelection}>
                                            Cancel
                                        </Button>
                                        <Button
                                            outlined={true}
                                            onClick={savePreset}
                                            before={<BooleanImage value={true} />}
                                        >
                                            Store chosen items
                                        </Button>
                                    </>
                                )}

                                {!isSelecting && <Button onClick={enableSelection}>Save review</Button>}
                            </>
                        </TransitionComponent>
                    </div>
                </div>
                // </TransitionComponent>
            )}

            {error && (
                <Response type={'error'} aligned={false} setResponse={setError}>
                    An error occurred.
                </Response>
            )}
        </>
    );
};

export default TaxOptimisation;
